// Typed view over app/public/data/manifest.json (the single source of truth
// shared with the pipeline). Only the fields the app consumes are typed.

// Runtime-only import (type imports in ./data are erased, so there is no
// runtime import cycle): configure the data-layer slice geometry on load.
import { setSliceLayout } from "./data";

export interface Allergen {
  code: string;
  english: string;
  latin: string;
  files: string[]; // 3 strips: *_p0.png, *_p1.png, *_p2.png (12 slices each)
  defaultSelected: boolean;
}

export interface TimeSlice {
  index: number;
  // Weekly slices carry `week` (1..52); legacy dekadal slices carried
  // `month`/`period`. All are optional so the UI can consume either shape and
  // simply render `label`.
  week?: number;
  month?: number;
  period?: number;
  label: string; // date range, e.g. "11 Apr - 17 Apr" (weekly) or dekadal
  isoStart: string;
  isoEnd: string;
}

export interface PaletteLevel {
  level: number;
  color: string; // #RRGGBB
  byteValue: number; // 0,64,128,192,255
  label_en: string;
  label_de: string;
}

export interface GeoBlock {
  selected_model: string;
  params: {
    model: string;
    // 0 equirect, 1 web_mercator, 2 LCC, 3 mesh_lookup, 4 etrs89_laea_3035,
    // 5 stereographic. See app/src/projections.ts (mirrors georef/models.py).
    uProjModel: number;
    // row-major 2x3: [[a,b,c],[d,e,f]] -> sx=a*X+b*Y+c, sy=d*X+e*Y+f where
    // (X,Y) = geoToPlanar(uProjModel, params, lon, lat). For uProjModel=0
    // (equirect) X=lon, Y=lat so this is the historical lon/lat affine.
    affine: [[number, number, number], [number, number, number]];
    // --- intrinsic projection params (present only for the chosen family) ---
    // All optional + additive so the equirect block stays back-compatible.
    lon0?: number; // central meridian (deg) — LCC/LAEA/stereographic
    lat0?: number; // latitude of origin (deg) — LCC/LAEA/stereographic
    lat1?: number; // LCC standard parallel 1
    lat2?: number; // LCC standard parallel 2
    k0?: number; // stereographic scale at centre
    mode?: "oblique" | "polar_north" | "polar_south"; // stereographic centre
    affine_layout?: string;
    planar_note?: string;
  };
  image_width: number;
  image_height: number;
  bbox: {
    lon_min?: number;
    lon_max?: number;
    lat_min?: number;
    lat_max?: number;
    corners: {
      top_left: { lon: number; lat: number };
      top_right: { lon: number; lat: number };
      bottom_right: { lon: number; lat: number };
      bottom_left: { lon: number; lat: number };
    };
  };
  offset_correction: {
    dx: number;
    dy: number;
    sx_scale: number;
    sy_scale: number;
    rot: number;
  };
}

/**
 * Change C — a confidence sub-area (plan §1.1-C). Coarse fallback / legend
 * labels for regions whose tier varies across sub-areas, when no per-pixel CONF
 * raster is shipped. All-optional; absent on every existing manifest.
 */
export interface ConfidenceZone {
  bbox: {
    lon_min: number;
    lon_max: number;
    lat_min: number;
    lat_max: number;
  };
  /** One of the 4-enum tiers; validated by parseConfidence's enum guard. */
  confidence: string;
  label?: string;
}

/**
 * Change C — declares the OPTIONAL per-pixel confidence raster (plan §1.1-D).
 * The byte values are recovered with the SAME byte->level decode FORMULA used
 * for the pollen strips (data.ts/pollenLayer.ts) and mapped to a tier via
 * byteToTier. Absent on every existing manifest, so the new CONF fetch/decode
 * plumbing is inactive for them (non-regression by construction).
 */
export interface ConfidenceRaster {
  /** Per-strip files (4x13 layout, week-varying) — mutually exclusive w/ file. */
  files?: string[];
  /** A single static (52-invariant) raster — the common case. */
  file?: string;
  /** True when `file` is a static 52-invariant raster. */
  static?: boolean;
  /** Byte ({0,64,128,192,255}) -> tier string. NO_DATA byte (0) is implicit. */
  byteToTier?: Record<string, string>;
}

/**
 * One entry of `provenance.sources[]` (plan: whole-Earth attribution surfacing).
 * The array is HETEROGENEOUS: most entries describe a measuring station (carry
 * `station`/`city`/`network`/`license`), while a handful describe an upstream
 * DATASET (carry `source` + `citation`, and — for licence-mandated notices — an
 * exact `attribution` string flagged `in_app_required: true`). ALL fields are
 * optional so the loosely-typed, varied real manifests type-check and so the
 * existing EU/NA/Asia manifests (which carry no `sources`) are unaffected.
 */
export interface ProvenanceSource {
  /** Dataset display name (dataset-type entries). */
  source?: string;
  /** Station display name (station-type entries). */
  station?: string;
  city?: string;
  /** Licence string (free-text). */
  license?: string;
  /** Full bibliographic citation (dataset-type entries) — shown as a credit. */
  citation?: string;
  url?: string;
  /**
   * Licence-mandated, must-be-visible attribution text. Rendered VERBATIM when
   * `in_app_required` is true (e.g. the Copernicus/ERA5-Land disclaimer, the
   * ESA WorldCover credit, or — flagged `displayed_data` — the region's own
   * primary CC-BY pollen-data credit).
   */
  attribution?: string;
  /** When true, `attribution` is legally required to appear in-app. */
  in_app_required?: boolean;
  /**
   * True for the entry that credits the region's PRIMARY DISPLAYED pollen data
   * (vs a supporting satellite/climate dataset). The About panel surfaces these
   * FIRST so each region leads with the credit for the data the user is seeing.
   */
  displayed_data?: boolean;
}

/**
 * Optional manifest `provenance` block. Read DEFENSIVELY — its real shape varies
 * a lot per region; only the few human-readable fields the About panel surfaces
 * are typed, all optional. Absent on EU/NA; present-but-without-`sources` on
 * East-Asia; full (`sources` + `b0_model`) on the five whole-Earth fill regions.
 */
export interface ManifestProvenance {
  method?: string;
  honesty?: string;
  note?: string;
  sources?: ProvenanceSource[];
  /** Global void-fill model descriptor (fill regions only); not surfaced as a credit. */
  b0_model?: Record<string, unknown>;
}

export interface Manifest {
  geo: GeoBlock;
  /** Optional per-region provenance (datasets, stations, required attributions). */
  provenance?: ManifestProvenance;
  // Optional region identity (multi-region build). Present on the NA + Asia
  // manifests; absent (=> Europe) on the original manifest.json.
  region?: string;
  regionLabel?: string;
  // --- Change C: confidence schema additions (plan §1.1; ALL OPTIONAL). ---
  // Existing manifests (EU/NA/Asia) carry NONE of these, so parseConfidence
  // returns undefined for them => no badge, no overlay (the non-regression
  // linchpin). `confidence` is the region's DOMINANT tier as a PLAIN STRING in
  // the 4-enum {measured,literature,model,synthetic}; the object {tier:...} form
  // is FORBIDDEN project-wide (plan §1.1 / audit BLOCKER 1.1).
  /** Legacy decorative mirror (REAL|MIXED|MODEL|SYNTHETIC); NEVER a badge trigger. */
  dataStatus?: string;
  /** The region's dominant confidence tier — a plain enum string. */
  confidence?: string;
  /** Worst tier present anywhere in the region (lets the UI warn). */
  confidenceFloor?: string;
  /** Sub-area tier overrides where no per-pixel raster ships. */
  confidenceZones?: ConfidenceZone[];
  /** Optional per-pixel confidence raster (finer spatial signal). */
  confidenceRaster?: ConfidenceRaster;
  dims: { width: number; height: number };
  nAllergens: number;
  nTimeSlices: number;
  dataDir: string;
  allergens: Allergen[];
  timeSlices: TimeSlice[];
  palette: { levels: PaletteLevel[] };
  encoding: {
    stripCount: number;
    slicesPerStrip: number;
    sliceWidth: number;
    sliceHeight: number;
  };
  noData: {
    maskFile: string;
    // Hi-res transparency/land mask (C1 fix). Present once the pipeline's
    // transparency_mask stage has run; the app falls back to maskFile if absent.
    maskFileHires?: string;
    maskHiresWidth?: number;
    maskHiresHeight?: number;
  };
}

export async function loadManifest(base = "./data/"): Promise<Manifest> {
  const r = await fetch(`${base}manifest.json`);
  if (!r.ok) throw new Error(`manifest.json -> HTTP ${r.status}`);
  const manifest = (await r.json()) as Manifest;
  // Configure the data-layer slice geometry (slice count, strips-per-allergen,
  // slice dims) from the manifest so the count is read DYNAMICALLY and is not
  // hard-coded. Done here — the single manifest-load entry point — so every
  // downstream consumer (decode + the pollen layer's texture array, which read
  // the live `let` bindings exported by ./data) sees the right values before
  // first use. See app/src/data.ts:setSliceLayout.
  setSliceLayout(manifest);
  return manifest;
}

/** #RRGGBB -> [r,g,b] in 0..1. */
export function hexToRgb01(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}
